import React, { useState, useMemo } from 'react';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Pagination,
  Spinner,
  InputGroup,
  DropdownButton,
  Dropdown,
} from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaFileExcel, FaExclamationCircle } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import * as api from '~/Services/Service';

const RECORDS_PER_PAGE = 20;

const codigoOpcional = Yup.number()
  .transform((value, originalValue) =>
    originalValue === '' || originalValue == null ? undefined : value
  )
  .typeError('Código deve ser inteiro')
  .integer('Código deve ser inteiro')
  .min(1, 'Código deve ser ≥ 1');

const nomeOpcional = Yup.string().max(30, 'Nome deve ter até 30 caracteres');

const ramoFiltroSchema = Yup.object().shape({
  tipoFiltro: Yup.string().oneOf(['codigo', 'nome']).required(),
  inicio: Yup.mixed().when('tipoFiltro', {
    is: 'codigo',
    then: codigoOpcional,
    otherwise: nomeOpcional,
  }),
  fim: Yup.mixed().when('tipoFiltro', {
    is: 'codigo',
    then: codigoOpcional,
    otherwise: nomeOpcional,
  }),
});

const initialValues = { tipoFiltro: 'codigo', inicio: '', fim: '' };

const colunas = [
  { title: 'Código', key: 'codigo', ordenacao: true, width: '150px' },
  { title: 'Descrição', key: 'descricao', ordenacao: true },
];

const RelatorioRamos = () => {
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [buscaRealizada, setBuscaRealizada] = useState(false);
  const [ordenacao, setOrdenacao] = useState({
    coluna: 'codigo',
    direcao: 'asc',
  });

  const buscarRamos = async (filtro) => {
    try {
      setLoading(true);
      const data = await api.listarRamosFiltrado(filtro);
      setResultados(Array.isArray(data) ? data : []);
      setPagina(1);
      setBuscaRealizada(true);
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao buscar ramos de atividade';
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values) => {
    const { tipoFiltro, inicio, fim } = values;
    const isCodigo = tipoFiltro === 'codigo';
    const filtro = {};
    if (inicio !== '' && inicio != null) {
      filtro[isCodigo ? 'codInicio' : 'nomInicio'] = inicio;
    }
    if (fim !== '' && fim != null) {
      filtro[isCodigo ? 'codFim' : 'nomFim'] = fim;
    }
    buscarRamos(filtro);
  };

  const resultadosOrdenados = useMemo(() => {
    if (!ordenacao.coluna) return resultados;
    return [...resultados].sort((a, b) => {
      const aVal = a[ordenacao.coluna] != null ? a[ordenacao.coluna] : '';
      const bVal = b[ordenacao.coluna] != null ? b[ordenacao.coluna] : '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return ordenacao.direcao === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const strA = String(aVal).toLowerCase();
      const strB = String(bVal).toLowerCase();
      if (strA < strB) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (strA > strB) return ordenacao.direcao === 'asc' ? 1 : -1;
      return 0;
    });
  }, [resultados, ordenacao]);

  const totalPaginas = Math.ceil(resultadosOrdenados.length / RECORDS_PER_PAGE);
  const paginaAtual = Math.min(pagina, totalPaginas || 1);

  const recordsAtuais = useMemo(() => {
    const fim = paginaAtual * RECORDS_PER_PAGE;
    const inicio = fim - RECORDS_PER_PAGE;
    return resultadosOrdenados.slice(inicio, fim);
  }, [resultadosOrdenados, paginaAtual]);

  const handleExportar = async () => {
    if (!resultados.length) return;
    try {
      setExportando(true);
      const blob = await api.exportarRelatorioRamosExcel(resultados);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
      )}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      link.download = `RelatorioRamos_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao exportar relatório';
      toast.error(mensagem);
    } finally {
      setExportando(false);
    }
  };

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const items = [];
    const maxVisible = 5;
    const start = Math.max(1, paginaAtual - Math.floor(maxVisible / 2));
    const end = Math.min(totalPaginas, start + maxVisible - 1);

    if (start > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setPagina(1)}>
          1
        </Pagination.Item>
      );
      if (start > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let p = start; p <= end; p += 1) {
      items.push(
        <Pagination.Item
          key={`pag-${p}`}
          active={p === paginaAtual}
          onClick={() => setPagina(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    if (end < totalPaginas) {
      if (end < totalPaginas - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <Pagination.Item
          key={totalPaginas}
          onClick={() => setPagina(totalPaginas)}
        >
          {totalPaginas}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="d-inline-flex mb-0">
        <Pagination.First
          onClick={() => setPagina(1)}
          disabled={paginaAtual === 1}
        />
        <Pagination.Prev
          onClick={() => setPagina(paginaAtual - 1)}
          disabled={paginaAtual === 1}
        />
        {items}
        <Pagination.Next
          onClick={() => setPagina(paginaAtual + 1)}
          disabled={paginaAtual === totalPaginas}
        />
        <Pagination.Last
          onClick={() => setPagina(totalPaginas)}
          disabled={paginaAtual === totalPaginas}
        />
      </Pagination>
    );
  };

  const renderFiltro = () => (
    <Formik
      initialValues={initialValues}
      validationSchema={ramoFiltroSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit: submitForm,
        setFieldValue,
      }) => {
        const isCodigo = values.tipoFiltro === 'codigo';
        const labelBase = isCodigo ? 'Código' : 'Nome';
        const inputType = isCodigo ? 'number' : 'text';
        const inputMaxLength = isCodigo ? undefined : 30;

        const trocarTipo = (novoTipo) => {
          if (novoTipo === values.tipoFiltro) return;
          setFieldValue('tipoFiltro', novoTipo);
          setFieldValue('inicio', '');
          setFieldValue('fim', '');
        };

        return (
          <Form noValidate onSubmit={submitForm}>
            <Row className="align-items-end">
              <Col xs={12} sm={6} md={3}>
                <label className="small mb-1">{`${labelBase} Inicial`}</label>
                <Form.Control
                  name="inicio"
                  type={inputType}
                  maxLength={inputMaxLength}
                  min={isCodigo ? 1 : undefined}
                  step={isCodigo ? 1 : undefined}
                  value={values.inicio}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.inicio && !!errors.inicio}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.inicio}
                </Form.Control.Feedback>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <label className="small mb-1">{`${labelBase} Final`}</label>
                <InputGroup>
                  <Form.Control
                    name="fim"
                    type={inputType}
                    maxLength={inputMaxLength}
                    min={isCodigo ? 1 : undefined}
                    step={isCodigo ? 1 : undefined}
                    value={values.fim}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.fim && !!errors.fim}
                  />
                  <DropdownButton
                    as={InputGroup.Append}
                    alignRight
                    variant="outline-secondary"
                    title={labelBase}
                    id="dropdown-tipo-filtro-ramos"
                  >
                    <Dropdown.Item
                      active={isCodigo}
                      onClick={() => trocarTipo('codigo')}
                    >
                      Código
                    </Dropdown.Item>
                    <Dropdown.Item
                      active={!isCodigo}
                      onClick={() => trocarTipo('nome')}
                    >
                      Nome
                    </Dropdown.Item>
                  </DropdownButton>
                  <Form.Control.Feedback type="invalid">
                    {errors.fim}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
              <Col
                xs={12}
                md="auto"
                className="mt-3 mt-md-0 mb-2 mb-md-0 ml-md-auto pr-md-1"
              >
                <Button
                  block
                  type="submit"
                  variant="primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      Buscando...
                    </>
                  ) : (
                    'Buscar'
                  )}
                </Button>
              </Col>
              <Col xs={12} md="auto" className="pl-md-1">
                <Button
                  block
                  type="button"
                  variant="success"
                  onClick={handleExportar}
                  disabled={!resultados.length || exportando || loading}
                >
                  {exportando ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      Exportando...
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="mr-1" /> Exportar Excel
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );

  const primeiroReg = (paginaAtual - 1) * RECORDS_PER_PAGE + 1;
  const ultimoReg = Math.min(
    paginaAtual * RECORDS_PER_PAGE,
    resultadosOrdenados.length
  );

  return (
    <div>
      <Header title="Relatório de Ramos de Atividade" />

      <Card className="mb-3">
        <Card.Body>{renderFiltro()}</Card.Body>
      </Card>

      {loading && (
        <div className="container-fluid bg-white p-5 mt-3 text-center">
          <Spinner animation="border" role="status" />
          <div className="mt-2 text-muted">Buscando ramos...</div>
        </div>
      )}

      {!loading && resultadosOrdenados.length > 0 && (
        <>
          <div className="container-fluid bg-white p-3 mt-3">
            <Row className="mb-2">
              <Col>
                <span>
                  Total de {resultadosOrdenados.length} registro(s)
                  encontrado(s)
                </span>
              </Col>
            </Row>
          </div>

          <div className="container-fluid bg-white p-3">
            <Tabela
              colunas={colunas}
              dados={recordsAtuais}
              striped
              hover
              ordenacao={ordenacao}
              onOrdenacaoChange={setOrdenacao}
            />
          </div>

          <div className="container-fluid bg-white p-3">
            <Row className="mt-2">
              <Col className="d-flex align-items-center flex-wrap">
                <span
                  className="text-muted mr-3"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Mostrando {primeiroReg} até {ultimoReg} de{' '}
                  {resultadosOrdenados.length} registros
                </span>
                {renderPaginacao()}
              </Col>
            </Row>
          </div>
        </>
      )}

      {!loading && buscaRealizada && resultadosOrdenados.length === 0 && (
        <div className="container-fluid bg-white p-5 mt-3 text-center">
          <Row>
            <Col>
              <FaExclamationCircle size={48} className="text-muted mb-3" />
              <h5 className="text-muted">
                Nenhum ramo encontrado para o filtro informado
              </h5>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default RelatorioRamos;
